import React, { useState } from "react";
import { Button, message, Spin } from "antd";
import { WizardProgress } from "../components/WizardProgress";
import { ReviewMappingStep2 } from "./ReviewMappingStep2";
import { ReviewMappingStep3 } from "./ReviewMappingStep3";
import { MappingComplete } from "../components/MappingComplete";
import { MappingChange, PropertyGroups, TenantMapping } from "../Mapping";
import { getExternalMerchants } from "Services/Api/PciCompliance/Mapping.Service";
import { useTranslation } from "react-i18next";

interface ReviewMappingContainerProps {
  tenantInfo: TenantMapping;
  handleBackToSettings: () => void;
  handleBackToHome: () => void;
  venzaProperties: PropertyGroups[],
  initialData: MappingChange[];
}

const REVIEW_STEPS = [
  { number: 1, title: "Review Changes" },
  { number: 2, title: "Confirm & Save" },
];

export const ReviewMappingContainer: React.FC<ReviewMappingContainerProps> = ({
  tenantInfo,
  handleBackToSettings,
  handleBackToHome,
  initialData,
  venzaProperties,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [mappingCount, setMappingCount] = useState<number>(0);
  const [previousChanges, setPreviousChanges] = useState<MappingChange[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleReviewStep1Next = async (prevChanges: MappingChange[]) => {
    const mids = prevChanges
      .filter(el => !!el.pciPortalPropertyId && el?.action !== "remove")
      .map(el => el.pciPortalPropertyId);

    const result = await getExternalMerchants(
      Number(tenantInfo.id),
      Number(tenantInfo.providerId),
      "",
      mids.join(",")
    );

    const resultMap = new Map<string, any>();
    (result ?? []).forEach((r: any) => {
      resultMap.set(String(r.mid), r);
    });

    const initialIds = new Set(
      initialData.map(e => e.pciPortalPropertyId)
    );

    const updatedChanges: MappingChange[] = prevChanges.map(item => {
      // ignore items that already exist in initial data
      if (initialIds.has(item.pciPortalPropertyId)) return item;

      if (!item.pciPortalPropertyId) return item;

      const apiRow = resultMap.get(String(item.pciPortalPropertyId));

      if (apiRow && apiRow.mapping_status !== "unmapped") {
        return {
          ...item,
          pciPortalPropertyError: t("mapping.errors.alreadyMapped"),
        };
      }

      return {
        ...item,
        pciPortalPropertyError: undefined,
      };
    });

    const hasErrors = updatedChanges.some(
      item => !!item.pciPortalPropertyError
    );

    if (hasErrors) {
      message.warning(t("mapping.errors.alreadyMapped2"));
      setPreviousChanges(updatedChanges);
      return;
    }
    setPreviousChanges(updatedChanges);
    setCurrentStep(2);
  };



  const handleReviewConfirm = (count: number) => {
    setCurrentStep(3);
    setMappingCount(count);
  };

  if (currentStep === 3) {
    return (
      <div >
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={handleBackToSettings}
          >
            {t('mapping.table.backToMapSettings')}
          </Button>
        </div>

        <MappingComplete
          tenantInfo={tenantInfo}
          mappingCount={mappingCount}
          onBackToHome={handleBackToHome}
        />
      </div>
    );
  }

  return (
    <Spin spinning={isSubmitting}>
      <div className="mx-auto mw-896">
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={handleBackToSettings}
          >
            {t('mapping.table.backToMapSettings')}
          </Button>
        </div>

        <div
          className="mb-8"
        >
          <h1
            className="fs-15"
          >
            {t("mapping.reviewProperty.title")}
          </h1>
          <p className="text-muted-foreground fw-200 fs-1">
          {t("mapping.reviewProperty.subTitle")}
          </p>
        </div>

        <WizardProgress currentStep={currentStep} steps={REVIEW_STEPS.map((item) => ({...item, title: t(`mapping.reviewProperty.steps.${item.number}`)}))} />

        {currentStep === 1 && (
          <ReviewMappingStep2
            tenantInfo={tenantInfo}
            initialData={previousChanges.length > 0 ? previousChanges : initialData}
            unChangedData={initialData}
            venzaProperties={venzaProperties}
            onNext={handleReviewStep1Next}
            onBack={handleBackToSettings}
          />
        )}
        {currentStep === 2 && (
          <ReviewMappingStep3
            tenantInfo={tenantInfo}
            initialData={initialData.filter(e => e.id !== "add-row")}
            mappedchanges={previousChanges.filter((row, _, arr) => {
              const hasConflict = arr.some(
                other =>
                  other !== row &&
                  other.pciPortalPropertyId === row.pciPortalPropertyId &&
                  String(other.venzaPropertyId) === String(row.venzaPropertyId) &&
                  other.action !== row.action
              );
              return !hasConflict;
            }).filter(e => e.id !== "add-row")}
            onConfirm={handleReviewConfirm}
            backToSettings={handleBackToSettings}
            onBack={() => setCurrentStep(1)}
            setIsSubmitting={setIsSubmitting}
          />
        )}
      </div>
    </Spin>
  );
};

export default ReviewMappingContainer;
